import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { BANNER } from "@/lib/brand";
import { Button } from "@/components/ui/button";

const ease = [0.22, 1, 0.36, 1];

const INTRO_LINES = [
  "Ci sono luoghi in cui il tempo rallenta.",
  "Luoghi dove il rumore lascia spazio al vento tra gli alberi, al profumo del bosco dopo la pioggia, ai colori di un'alba o di un tramonto che sembrano fermare il mondo per qualche istante.",
  "Luoghi dove il respiro diventa più leggero, i pensieri si fanno più silenziosi e la natura ricorda, senza dire una parola, ciò che troppo spesso dimentichiamo.",
  "Ci ricorda che il silenzio ci mette in connessione con noi stessi.",
  "Che ogni cosa segue un ciclo.",
  "Che la natura cambia con il passare delle stagioni, riempiendosi ogni volta di colori e profumi diversi.",
  "Che il buio è il tempo del riposo e dei sogni.",
  "E che ognuno di noi nasce sotto un cielo diverso, portando con sé una storia unica.",
  "È da qui che nasce Incanti d'Estate.",
  "Dal desiderio di custodire tutto questo e trasformarlo in qualcosa che possa continuare a vivere anche tra le mura di una casa, con i suoi colori, i suoi profumi e i suoi simboli.",
  "Ogni volta che lo sguardo si poserà su un Incanto, potremo ritrovare, anche solo per un istante, quella pace, quella connessione con la natura e con noi stessi che la frenesia della vita quotidiana ci fa troppo spesso dimenticare.",
];

const CHAPTERS = [
  { n: "01", t: "Inizio del Cammino", d: "Ci sono istanti che non tornano, ma che meritano di essere custoditi per sempre. La nascita di un bambino, il suo Battesimo, la Prima Comunione, la Cresima. Questa collezione nasce per accompagnare con delicatezza i primi capitoli della sua storia, trasformandoli in un ricordo da custodire nel tempo." },
  { n: "02", t: "Il Ritmo della Terra", d: "La Terra ci insegna che ogni cosa ha il suo tempo. Le stagioni scandiscono il ritmo della vita, ricordandoci che ogni cambiamento porta con sé una nuova bellezza." },
  { n: "03", t: "La Bussola del Cielo", d: "Il cielo ci orienta. I segni zodiacali raccontano inclinazioni, qualità ed energie che ci accompagnano fin dalla nascita, come una bussola che ci aiuta a trovare la nostra direzione." },
  { n: "04", t: "Il Respiro della Luna", d: "Ci sono momenti in cui la vita ci chiede di rallentare, fermarci e respirare. Come la Luna cambia volto senza mai perdere la propria essenza, anche noi attraversiamo fasi diverse che ci preparano a ricominciare." },
  { n: "05", t: "Le Sette Ruote", d: "Le sette ruote rappresentano il movimento, l'evoluzione e l'armonia. Come i sette centri energetici del nostro corpo, ci ricordano che l'energia è viva, cambia forma e ci accompagna lungo tutto il nostro cammino." },
];

export default function Home() {
  const [collections, setCollections] = useState([]);
  const [manifestoOpen, setManifestoOpen] = useState(false);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative z-10">
      {/* HERO */}
      <section className="relative overflow-hidden pt-24">
        {/* INTRO CLICCABILE — sopra il banner */}
        <div className="text-center px-6 pb-6 md:pb-8">
          <button
            onClick={() => setManifestoOpen((v) => !v)}
            className="group inline-flex items-center gap-2 font-serif text-base md:text-lg tracking-[0.18em] uppercase text-gold hover:text-gold/80 transition-colors"
            data-testid="intro-manifesto-toggle"
            aria-expanded={manifestoOpen}
          >
            Per fare rumore c'è il resto del mondo
            <ChevronDown
              size={18}
              className={`transition-transform duration-500 ${manifestoOpen ? "rotate-180" : ""}`}
            />
          </button>

          <AnimatePresence initial={false}>
            {manifestoOpen && (
              <motion.div
                key="intro-text"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.7, ease }}
                className="overflow-hidden"
                data-testid="intro-manifesto-text"
              >
                <div className="max-w-2xl mx-auto pt-8 pb-4 text-left">
                  <p className="font-serif text-2xl md:text-3xl font-light text-foreground leading-relaxed">
                    Per fare rumore c'è il resto del mondo.
                  </p>
                  <div className="mt-10 space-y-6">
                    {INTRO_LINES.map((line, i) => (
                      <p key={i} className="text-base md:text-lg text-foreground/75 leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.img
          src={BANNER}
          alt="Incanti d'Estate — Laboratorio Botanico Alchemico"
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease }}
          className="w-full h-auto block"
          data-testid="hero-banner"
        />
        <div className="max-w-3xl mx-auto px-6 md:px-10 w-full flex flex-col items-center text-center py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center gap-10"
          >
            <p className="max-w-xl text-lg text-muted-foreground leading-relaxed">
              Le collezioni botaniche di Incanti d'Estate non sono semplici composizioni floreali. Sono un percorso ispirato al silenzio della Natura, ai suoi ritmi lenti e ai simboli che da sempre accompagnano il cammino dell'uomo. Ogni collezione racconta una tappa di questo viaggio.
            </p>
            <Link to="/shop">
              <Button className="rounded-full bg-gold hover:bg-gold/90 text-white h-14 px-8 text-base group" data-testid="hero-cta">
                Scopri le collezioni
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="font-serif text-4xl md:text-5xl font-light text-gold mb-4">Le collezioni</p>
          </div>
          <Link to="/shop" className="hidden md:flex items-center gap-2 text-sm hover:text-gold transition-colors group" data-testid="featured-view-all">
            Vedi tutto <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16" data-testid="home-collections-grid">
          {collections.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease }}
              data-testid={`home-collection-card-${c.slug}`}
            >
              <Link to={`/collezione/${c.slug}`} className="group block">
                <div className="relative overflow-hidden rounded-md bg-muted aspect-[4/5] mb-5">
                  <img src={c.cover_image} alt={c.name} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-3xl text-white leading-tight mb-1 drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">{c.name}</h3>
                    <span className="text-white/90 text-sm inline-flex items-center gap-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">
                      Scopri la collezione
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section
        id="manifesto"
        className="relative z-10 bg-[#F7F1E1] bg-no-repeat py-24 md:py-28"
        style={{
          backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/8d26b62b-ea4d-4550-ada7-9113162c3d10/images/87eb5be501ee3e1a28110e97eb7022dccf9253780f56f10bf96c3580f9fed1c1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1100px] mx-auto px-10 md:px-20">
          <div className="space-y-24">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease }}
                className={`grid md:grid-cols-12 gap-8 items-start ${i % 2 === 1 ? "md:text-right" : ""}`}
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-3 md:col-start-10" : ""}`}>
                  <span className="font-serif text-8xl md:text-9xl font-light text-[hsl(var(--gold))] leading-none">{c.n}</span>
                </div>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2 md:col-start-4" : "md:col-start-5"}`}>
                  <h3 className="font-serif text-4xl md:text-5xl font-light mb-5 text-foreground">{c.t}</h3>
                  <p className="text-lg text-foreground/70 leading-relaxed max-w-lg md:inline-block">{c.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
